import React, { useState, useMemo, useEffect } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSelectedCity } from '../contexts/SelectedCityContext';
import useDashboardData from '../hooks/useDashboardData';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';

// Constantes baseadas na Lei do ICMS Educacional da Paraíba (Lei nº 12.373/2022)
const ICMS_CONFIG = {
  LOW_PERFORMANCE: 400,
  MEDIUM_PERFORMANCE: 900,
  HIGH_PERFORMANCE: 1500,
  STATE_AVERAGE_INITIAL: 5.7,
  STATE_AVERAGE_FINAL: 4.5,
};

// Constantes FUNDEB - Parâmetros nacionais (valores de referência 2024)
const FUNDEB_CONFIG = {
  VAAT_MINIMO_NACIONAL: 5300.00,      // Valor mínimo VAAT nacional (R$/aluno/ano)
  VAAF_MINIMO_NACIONAL: 4500.00,      // Valor mínimo VAAF nacional (R$/aluno/ano)
  VAAR_VALOR_BASE: 500.00,            // Valor base por aluno quando elegível ao VAAR
  FATOR_PONDERACAO_MEDIO: 1.0,        // Fator médio de ponderação (pode variar por modalidade)
  IDEB_CRESCIMENTO_MINIMO: 0.3,       // Crescimento mínimo do IDEB para elegibilidade VAAR
};

const CAAPORA_OFFICIAL_IDEB = 4.9;

// Incrementos de 0.1 para simulação mais granular
const INCREMENT_OPTIONS = [];
for (let i = 0.1; i <= 2.0; i = Math.round((i + 0.1) * 10) / 10) {
  INCREMENT_OPTIONS.push({
    value: i,
    label: `+${i.toFixed(1)} ${i === 0.1 ? 'ponto' : 'pontos'}`,
  });
}

const calculateValuePerStudent = (ideb, stateAverage) => {
  if (!ideb || ideb < stateAverage - 0.5) {
    return ICMS_CONFIG.LOW_PERFORMANCE;
  } else if (ideb < stateAverage + 0.5) {
    return ICMS_CONFIG.MEDIUM_PERFORMANCE;
  } else {
    return ICMS_CONFIG.HIGH_PERFORMANCE;
  }
};

const getPerformanceLevel = (ideb, stateAverage) => {
  if (!ideb || ideb < stateAverage - 0.5) return 'low';
  if (ideb < stateAverage + 0.5) return 'medium';
  return 'high';
};

/**
 * Calcula Alunos Ponderados
 * @param {number} numAlunos - Número de matrículas
 * @param {number} fatorPonderacao - Fator de ponderação (padrão 1.0)
 * @returns {number} - Alunos ponderados
 */
const calcularAlunosPonderados = (numAlunos, fatorPonderacao = FUNDEB_CONFIG.FATOR_PONDERACAO_MEDIO) => {
  return numAlunos * fatorPonderacao;
};

/**
 * Calcula VAAT - Valor Anual Total por Aluno
 * VAAT = Receita Total da Educação ÷ Alunos Ponderados
 * @param {number} receitaTotalEducacao - Receita total da educação do município
 * @param {number} alunosPonderados - Número de alunos ponderados
 * @returns {object} - { vaat, complementacao, elegivel }
 */
const calcularVAAT = (receitaTotalEducacao, alunosPonderados) => {
  if (!alunosPonderados || alunosPonderados === 0) {
    return { vaat: 0, complementacao: 0, elegivel: false };
  }

  const vaat = receitaTotalEducacao / alunosPonderados;
  const elegivel = vaat < FUNDEB_CONFIG.VAAT_MINIMO_NACIONAL;
  const complementacao = elegivel
    ? (FUNDEB_CONFIG.VAAT_MINIMO_NACIONAL - vaat) * alunosPonderados
    : 0;

  return { vaat, complementacao, elegivel };
};

/**
 * Calcula VAAF - Valor Anual por Aluno FUNDEB
 * VAAF = Receita do FUNDEB ÷ Alunos Ponderados
 * @param {number} receitaFundeb - Receita do FUNDEB do município
 * @param {number} alunosPonderados - Número de alunos ponderados
 * @returns {object} - { vaaf, complementacao, elegivel }
 */
const calcularVAAF = (receitaFundeb, alunosPonderados) => {
  if (!alunosPonderados || alunosPonderados === 0) {
    return { vaaf: 0, complementacao: 0, elegivel: false };
  }

  const vaaf = receitaFundeb / alunosPonderados;
  const elegivel = vaaf < FUNDEB_CONFIG.VAAF_MINIMO_NACIONAL;
  const complementacao = elegivel
    ? (FUNDEB_CONFIG.VAAF_MINIMO_NACIONAL - vaaf) * alunosPonderados
    : 0;

  return { vaaf, complementacao, elegivel };
};

/**
 * Calcula VAAR - Valor Anual por Aluno por Resultado
 * Depende do cumprimento de condicionalidades (evolução positiva do IDEB)
 * @param {number} idebAtual - IDEB atual do município
 * @param {number} idebAnterior - IDEB da medição anterior
 * @param {number} alunosPonderados - Número de alunos ponderados
 * @returns {object} - { vaar, valorTotal, elegivel, crescimento }
 */
const calcularVAAR = (idebAtual, idebAnterior, alunosPonderados) => {
  const crescimento = idebAtual - idebAnterior;
  const elegivel = crescimento >= FUNDEB_CONFIG.IDEB_CRESCIMENTO_MINIMO;
  const valorPorAluno = elegivel ? FUNDEB_CONFIG.VAAR_VALOR_BASE : 0;
  const valorTotal = valorPorAluno * alunosPonderados;

  return {
    vaar: valorPorAluno,
    valorTotal,
    elegivel,
    crescimento
  };
};

const calculateProjection = (studentCount, currentIdeb, stateAverage, increment, extras, fundebParams) => {
  const { vaarManual, vaatManual, vaafManual, vaafEstadual } = extras;
  const { receitaTotalEducacao, receitaFundeb, fatorPonderacao, idebAnterior, modoCalculo } = fundebParams;

  const currentValue = calculateValuePerStudent(currentIdeb, stateAverage);
  const projectedIdeb = currentIdeb + increment;
  const projectedValue = calculateValuePerStudent(projectedIdeb, stateAverage);

  const currentRevenue = studentCount * currentValue;
  const projectedRevenue = studentCount * projectedValue;
  const additionalRevenue = projectedRevenue - currentRevenue;

  // Calcula alunos ponderados
  const alunosPonderados = calcularAlunosPonderados(studentCount, fatorPonderacao);

  // Cálculos FUNDEB automáticos
  let vaarCalc = { vaar: 0, valorTotal: 0, elegivel: false, crescimento: 0 };
  let vaatCalc = { vaat: 0, complementacao: 0, elegivel: false };
  let vaafCalc = { vaaf: 0, complementacao: 0, elegivel: false };

  if (modoCalculo === 'automatico') {
    // VAAR - considera o incremento projetado como evolução do IDEB
    vaarCalc = calcularVAAR(projectedIdeb, idebAnterior, alunosPonderados);

    // VAAT
    if (receitaTotalEducacao > 0) {
      vaatCalc = calcularVAAT(receitaTotalEducacao, alunosPonderados);
    }

    // VAAF
    if (receitaFundeb > 0) {
      vaafCalc = calcularVAAF(receitaFundeb, alunosPonderados);
    }
  }

  // Usa valores calculados ou manuais dependendo do modo
  const vaarFinal = modoCalculo === 'automatico' ? vaarCalc.valorTotal : (vaarManual || 0);
  const vaatFinal = modoCalculo === 'automatico' ? vaatCalc.complementacao : (vaatManual || 0);
  const vaafFinal = modoCalculo === 'automatico' ? vaafCalc.complementacao : (vaafManual || 0);

  // Extras que influenciam no valor final
  const totalExtras = vaarFinal + vaatFinal + vaafFinal + (vaafEstadual || 0);
  const projectedRevenueWithExtras = projectedRevenue + totalExtras;
  const totalGain = additionalRevenue + totalExtras;

  return {
    currentIdeb,
    projectedIdeb,
    currentValue,
    projectedValue,
    currentRevenue,
    projectedRevenue,
    projectedRevenueWithExtras,
    additionalRevenue,
    totalExtras,
    totalGain,
    fourYearImpact: totalGain * 4,
    currentLevel: getPerformanceLevel(currentIdeb, stateAverage),
    projectedLevel: getPerformanceLevel(projectedIdeb, stateAverage),
    // Dados FUNDEB detalhados
    fundeb: {
      alunosPonderados,
      vaar: vaarCalc,
      vaat: vaatCalc,
      vaaf: vaafCalc,
      vaarFinal,
      vaatFinal,
      vaafFinal,
    },
  };
};

function SimuladorFinanceiro() {
  const { getUserCity, hasRole } = useAuth();
  const { darkMode } = useTheme();
  const userCity = getUserCity();
  const isAdmin = hasRole(ROLES.ADMIN, ROLES.GESTOR, ROLES.COORDENADOR);

  // Cidade selecionada vem do sidebar (SelectedCityContext)
  const { cities, selectedCityId } = useSelectedCity();

  const [selectedIncrement, setSelectedIncrement] = useState(0.5);

  // Valores manuais (modo manual)
  const [vaarManual, setVaarManual] = useState(0);
  const [vaatManual, setVaatManual] = useState(0);
  const [vaafManual, setVaafManual] = useState(0);
  const [vaafEstadual, setVaafEstadual] = useState(0);

  // Parâmetros editáveis
  const [manualStudentCount, setManualStudentCount] = useState(null);
  const [manualIdeb, setManualIdeb] = useState(null);
  const [manualStateAverage, setManualStateAverage] = useState(null);

  // Novos parâmetros FUNDEB
  const [modoCalculo, setModoCalculo] = useState('automatico'); // 'automatico' ou 'manual'
  const [receitaTotalEducacao, setReceitaTotalEducacao] = useState(0);
  const [receitaFundeb, setReceitaFundeb] = useState(0);
  const [fatorPonderacao, setFatorPonderacao] = useState(FUNDEB_CONFIG.FATOR_PONDERACAO_MEDIO);
  const [idebAnterior, setIdebAnterior] = useState(4.5);

  const selectedCityName = selectedCityId
    ? cities.find(c => c.id === selectedCityId)?.city
    : null;

  const { stats, loading } = useDashboardData({ cityId: selectedCityId });

  const dbStudentCount = stats?.totals?.students || 0;
  const currentIdebRaw = stats?.ideb?.average || null;
  const isUsingFallback = currentIdebRaw === null || currentIdebRaw === undefined;
  const dbIdeb = isUsingFallback ? CAAPORA_OFFICIAL_IDEB : currentIdebRaw;

  // Usa valores manuais se definidos, senão usa do banco
  const studentCount = manualStudentCount !== null ? manualStudentCount : dbStudentCount;
  const effectiveIdeb = manualIdeb !== null ? manualIdeb : dbIdeb;
  const stateAverage = manualStateAverage !== null ? manualStateAverage : ICMS_CONFIG.STATE_AVERAGE_INITIAL;
  const cityName = selectedCityName || userCity?.name || 'Todas as Cidades';

  const extras = useMemo(() => ({
    vaarManual,
    vaatManual,
    vaafManual,
    vaafEstadual
  }), [vaarManual, vaatManual, vaafManual, vaafEstadual]);

  const fundebParams = useMemo(() => ({
    receitaTotalEducacao,
    receitaFundeb,
    fatorPonderacao,
    idebAnterior,
    modoCalculo,
  }), [receitaTotalEducacao, receitaFundeb, fatorPonderacao, idebAnterior, modoCalculo]);

  const projection = useMemo(() => {
    return calculateProjection(studentCount, effectiveIdeb, stateAverage, selectedIncrement, extras, fundebParams);
  }, [studentCount, effectiveIdeb, stateAverage, selectedIncrement, extras, fundebParams]);

  const allScenarios = useMemo(() => {
    const subset = [0.1, 0.2, 0.3, 0.5, 0.7, 1.0, 1.5, 2.0];
    return subset.map(value => ({
      value,
      label: `+${value.toFixed(1)}`,
      ...calculateProjection(studentCount, effectiveIdeb, stateAverage, value, extras, fundebParams),
    }));
  }, [studentCount, effectiveIdeb, stateAverage, extras, fundebParams]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

  const formatCurrencyDecimal = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

  const formatNumber = (value) =>
    new Intl.NumberFormat('pt-BR').format(value);

  const getIdebStatus = (ideb) => {
    if (ideb >= stateAverage) return { label: 'Acima da media', color: 'text-[#13983D]', bg: 'bg-[#13983D]/10' };
    if (ideb >= stateAverage - 0.5) return { label: 'Na media', color: 'text-yellow-500', bg: 'bg-yellow-500/10' };
    return { label: 'Abaixo da media', color: 'text-red-500', bg: 'bg-red-500/10' };
  };

  const currentStatus = getIdebStatus(projection.currentIdeb);
  const projectedStatus = getIdebStatus(projection.projectedIdeb);

  const cardClass = (extra) => `rounded-2xl p-6 backdrop-blur-xl border ${darkMode ? 'bg-slate-800/70 border-slate-700/10' : 'bg-white/90 border-slate-200 shadow-sm'}`;
  const labelClass = `text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`;
  const inputClass = `w-full px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-200 ${darkMode
    ? 'bg-slate-700/50 border-slate-600/50 text-white focus:border-sideb-blue focus:ring-2 focus:ring-sideb-blue/20'
    : 'bg-white border-slate-200 text-slate-800 focus:border-sideb-blue focus:ring-2 focus:ring-sideb-blue/20'}`;

  return (
    <>
      {/* Header */}
      <header className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
              ? 'bg-gradient-to-r from-gray-50 to-gray-400'
              : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'}`}>
              Simulador Financeiro IDEB + FUNDEB
            </h1>
            <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
              Calcule o impacto financeiro do IDEB e complementações FUNDEB (VAAR, VAAT, VAAF)
            </p>
          </div>
        </div>
      </header>

      {loading ? (
        <LoadingSpinnerAdvanced
          darkMode={darkMode}
          message="Carregando Simulador..."
          subtitle="Calculando as projeções financeiras..."
          size="lg"
        />
      ) : (
        <>
          {/* Card principal - VALOR A RECEBER */}
          <div className={`rounded-3xl p-8 md:p-10 mb-8 border-2 ${darkMode
            ? 'bg-gradient-to-br from-[#13983D]/40 via-[#13983D]/30 to-[#0974BA]/30 border-[#13983D]/30'
            : 'bg-gradient-to-br from-[#13983D]/5 via-[#13983D]/5 to-[#0974BA]/5 border-[#13983D]/30'}`}>
            <div className="text-center">
              <p className={`text-sm font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>
                Valor Total Projetado (Anual)
              </p>
              <p className={`text-5xl md:text-6xl font-black mb-3 ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>
                {formatCurrency(projection.projectedRevenueWithExtras)}
              </p>
              <p className={`text-base mb-6 ${darkMode ? 'text-[#13983D]/70' : 'text-[#13983D]/70'}`}>
                Com IDEB projetado de <strong>{projection.projectedIdeb.toFixed(1)}</strong> | {cityName}
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <div className="text-center">
                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Receita Base IDEB</p>
                  <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                    {formatCurrency(projection.projectedRevenue)}
                  </p>
                </div>
                <div className="text-center">
                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Complementações FUNDEB</p>
                  <p className={`text-xl font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                    +{formatCurrency(projection.totalExtras)}
                  </p>
                </div>
                <div className="text-center">
                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Impacto 4 Anos (Mandato)</p>
                  <p className={`text-xl font-bold ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>
                    {formatCurrency(projection.fourYearImpact)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Fallback warning */}
          {isUsingFallback && (
            <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${darkMode
              ? 'bg-amber-900/30 border border-amber-500/30'
              : 'bg-amber-50 border border-amber-200'}`}>
              <span className="text-xl">⚠️</span>
              <p className={`text-sm ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                <strong>IDEB de referência:</strong> Usando o IDEB oficial de Caapora (4.9) como base.
                Cadastre os indicadores IDEB das escolas para cálculos mais precisos.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Painel de Parametros */}
            <div className={cardClass()}>
              <h3 className={`text-base font-semibold mb-5 flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                Parâmetros da Simulação
              </h3>

              {/* Modo de Cálculo */}
              <div className="mb-5">
                <label className={`block text-xs font-medium mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Modo de Cálculo FUNDEB
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setModoCalculo('automatico')}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${modoCalculo === 'automatico'
                        ? 'bg-sideb-blue text-white'
                        : darkMode
                          ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                  >
                    Automático
                  </button>
                  <button
                    type="button"
                    onClick={() => setModoCalculo('manual')}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${modoCalculo === 'manual'
                        ? 'bg-sideb-blue text-white'
                        : darkMode
                          ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                  >
                    Manual
                  </button>
                </div>
              </div>

              {/* Dados base - editaveis */}
              <div className="space-y-3 mb-5">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Número de Alunos (Matrículas)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={manualStudentCount !== null ? manualStudentCount : dbStudentCount}
                    onChange={(e) => setManualStudentCount(e.target.value === '' ? null : parseInt(e.target.value) || 0)}
                    placeholder={`${dbStudentCount} (do banco)`}
                    className={inputClass}
                  />
                  {manualStudentCount !== null && manualStudentCount !== dbStudentCount && (
                    <button type="button" onClick={() => setManualStudentCount(null)} className={`text-xs mt-1 ${darkMode ? 'text-sideb-blue' : 'text-sideb-blue'} hover:underline`}>
                      Restaurar valor do banco ({formatNumber(dbStudentCount)})
                    </button>
                  )}
                </div>

                {modoCalculo === 'automatico' && (
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Fator de Ponderação
                    </label>
                    <input
                      type="number"
                      min="0.5"
                      max="1.5"
                      step="0.05"
                      value={fatorPonderacao}
                      onChange={(e) => setFatorPonderacao(parseFloat(e.target.value) || 1.0)}
                      className={inputClass}
                    />
                    <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Alunos ponderados: {formatNumber(projection.fundeb.alunosPonderados.toFixed(0))}
                    </p>
                  </div>
                )}

                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    IDEB Atual
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    value={manualIdeb !== null ? manualIdeb : dbIdeb}
                    onChange={(e) => setManualIdeb(e.target.value === '' ? null : parseFloat(e.target.value) || 0)}
                    placeholder={`${dbIdeb} (do banco)`}
                    className={inputClass}
                  />
                  {manualIdeb !== null && manualIdeb !== dbIdeb && (
                    <button type="button" onClick={() => setManualIdeb(null)} className={`text-xs mt-1 ${darkMode ? 'text-sideb-blue' : 'text-sideb-blue'} hover:underline`}>
                      Restaurar valor do banco ({dbIdeb.toFixed(1)})
                    </button>
                  )}
                </div>

                {modoCalculo === 'automatico' && (
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      IDEB Anterior (para cálculo VAAR)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={idebAnterior}
                      onChange={(e) => setIdebAnterior(parseFloat(e.target.value) || 0)}
                      className={inputClass}
                    />
                  </div>
                )}

                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Média Estadual (PB)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    value={manualStateAverage !== null ? manualStateAverage : ICMS_CONFIG.STATE_AVERAGE_INITIAL}
                    onChange={(e) => setManualStateAverage(e.target.value === '' ? null : parseFloat(e.target.value) || 0)}
                    placeholder={`${ICMS_CONFIG.STATE_AVERAGE_INITIAL} (padrao)`}
                    className={inputClass}
                  />
                  {manualStateAverage !== null && manualStateAverage !== ICMS_CONFIG.STATE_AVERAGE_INITIAL && (
                    <button type="button" onClick={() => setManualStateAverage(null)} className={`text-xs mt-1 ${darkMode ? 'text-sideb-blue' : 'text-sideb-blue'} hover:underline`}>
                      Restaurar padrao ({ICMS_CONFIG.STATE_AVERAGE_INITIAL})
                    </button>
                  )}
                </div>

                <div className={`p-3 rounded-xl ${darkMode ? 'bg-[#13983D]/20 border border-[#13983D]/20' : 'bg-[#13983D]/5 border border-[#13983D]/20'}`}>
                  <p className={`text-xs mb-1 ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>IDEB Projetado</p>
                  <p className={`text-lg font-bold ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>{projection.projectedIdeb.toFixed(1)}</p>
                </div>
              </div>

              {/* Incremento IDEB - Slider */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <label className={labelClass}>Crescimento IDEB</label>
                  <span className={`text-sm font-bold px-3 py-1 rounded-lg ${darkMode ? 'bg-sideb-blue/20 text-sideb-blue' : 'bg-sideb-blue/10 text-sideb-blue'}`}>
                    +{selectedIncrement.toFixed(1)}
                  </span>
                </div>
                <input
                  type="range"
                  min="0.1"
                  max="2.0"
                  step="0.1"
                  value={selectedIncrement}
                  onChange={(e) => setSelectedIncrement(parseFloat(e.target.value))}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-sideb-blue"
                  style={{ background: darkMode ? '#334155' : '#e2e8f0' }}
                />
                <div className="flex justify-between mt-1">
                  <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>+0.1</span>
                  <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>+1.0</span>
                  <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>+2.0</span>
                </div>
              </div>

              {/* Parâmetros FUNDEB - Modo Automático */}
              {modoCalculo === 'automatico' && (
                <div className="space-y-4 mb-5">
                  <h4 className={`text-sm font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Dados Financeiros do Município
                  </h4>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Receita Total da Educação (R$/ano)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="100000"
                      value={receitaTotalEducacao || ''}
                      onChange={(e) => setReceitaTotalEducacao(parseFloat(e.target.value) || 0)}
                      placeholder="Ex: 50000000"
                      className={inputClass}
                    />
                    <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Para cálculo do VAAT
                    </p>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Receita do FUNDEB (R$/ano)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="100000"
                      value={receitaFundeb || ''}
                      onChange={(e) => setReceitaFundeb(parseFloat(e.target.value) || 0)}
                      placeholder="Ex: 30000000"
                      className={inputClass}
                    />
                    <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Para cálculo do VAAF
                    </p>
                  </div>
                </div>
              )}

              {/* Parametros extras - Modo Manual */}
              {modoCalculo === 'manual' && (
                <div className="space-y-4">
                  <h4 className={`text-sm font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Valores Adicionais (Manual)
                  </h4>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      VAAR - Valor por Resultado
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={vaarManual || ''}
                      onChange={(e) => setVaarManual(parseFloat(e.target.value) || 0)}
                      placeholder="R$ 0"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      VAAT - Complementacao
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={vaatManual || ''}
                      onChange={(e) => setVaatManual(parseFloat(e.target.value) || 0)}
                      placeholder="R$ 0"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      VAAF - Complementacao FUNDEB
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={vaafManual || ''}
                      onChange={(e) => setVaafManual(parseFloat(e.target.value) || 0)}
                      placeholder="R$ 0"
                      className={inputClass}
                    />
                  </div>
                </div>
              )}

              {/* VAAF Estadual - sempre visível */}
              <div className="mt-4">
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  VAAF Estadual (adicional)
                </label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={vaafEstadual || ''}
                  onChange={(e) => setVaafEstadual(parseFloat(e.target.value) || 0)}
                  placeholder="R$ 0"
                  className={inputClass}
                />
              </div>
            </div>

            {/* Coluna central e direita - Resultados */}
            <div className="lg:col-span-2 space-y-6">
              {/* Comparativo IDEB Atual vs Projetado */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* IDEB Atual */}
                <div className={`rounded-2xl p-6 border-2 ${darkMode ? 'border-slate-600/50 bg-slate-800/70' : 'border-slate-200 bg-white shadow-sm'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      IDEB Atual
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${currentStatus.bg} ${currentStatus.color}`}>
                      {currentStatus.label}
                    </span>
                  </div>
                  <p className={`text-4xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                    {projection.currentIdeb.toFixed(1)}
                  </p>
                  <div className={`text-xs space-y-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <p>Valor/aluno: <span className="font-semibold">{formatCurrency(projection.currentValue)}/ano</span></p>
                    <p>Receita anual: <span className="font-semibold">{formatCurrency(projection.currentRevenue)}</span></p>
                  </div>
                </div>

                {/* IDEB Projetado */}
                <div className={`rounded-2xl p-6 border-2 border-[#13983D]/50 ${darkMode ? 'bg-[#13983D]/20' : 'bg-[#13983D]/5'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className={`text-xs font-medium ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>
                      IDEB Projetado (+{selectedIncrement.toFixed(1)})
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${projectedStatus.bg} ${projectedStatus.color}`}>
                      {projectedStatus.label}
                    </span>
                  </div>
                  <p className={`text-4xl font-bold mb-2 ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>
                    {projection.projectedIdeb.toFixed(1)}
                  </p>
                  <div className={`text-xs space-y-1 ${darkMode ? 'text-[#13983D]/70' : 'text-[#13983D]/70'}`}>
                    <p>Valor/aluno: <span className="font-semibold">{formatCurrency(projection.projectedValue)}/ano</span></p>
                    <p>Receita anual: <span className="font-semibold">{formatCurrency(projection.projectedRevenue)}</span></p>
                  </div>
                </div>
              </div>

              {/* Detalhamento FUNDEB - Modo Automático */}
              {modoCalculo === 'automatico' && (
                <div className={`rounded-2xl p-6 ${darkMode
                  ? 'bg-gradient-to-r from-[#2E4974]/20 to-[#2E4974]/20 border border-[#2E4974]/30'
                  : 'bg-gradient-to-r from-[#2E4974]/5 to-[#2E4974]/5 border border-[#2E4974]/20 shadow-sm'}`}>
                  <h4 className={`font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                    Cálculos FUNDEB Detalhados
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* VAAR */}
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-800/50' : 'bg-white/70'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-xs font-semibold ${darkMode ? 'text-[#2E4974]' : 'text-[#2E4974]'}`}>VAAR</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${projection.fundeb.vaar.elegivel
                            ? 'bg-[#13983D]/20 text-[#13983D]'
                            : 'bg-red-500/20 text-red-500'
                          }`}>
                          {projection.fundeb.vaar.elegivel ? 'Elegível' : 'Não elegível'}
                        </span>
                      </div>
                      <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                        {formatCurrency(projection.fundeb.vaarFinal)}
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Crescimento IDEB: {projection.fundeb.vaar.crescimento.toFixed(1)} pts
                      </p>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        (mínimo necessário: {FUNDEB_CONFIG.IDEB_CRESCIMENTO_MINIMO} pts)
                      </p>
                    </div>

                    {/* VAAT */}
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-800/50' : 'bg-white/70'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-xs font-semibold ${darkMode ? 'text-[#2E4974]' : 'text-[#2E4974]'}`}>VAAT</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${projection.fundeb.vaat.elegivel
                            ? 'bg-[#13983D]/20 text-[#13983D]'
                            : 'bg-slate-500/20 text-slate-500'
                          }`}>
                          {projection.fundeb.vaat.elegivel ? 'Com complementação' : 'Sem complementação'}
                        </span>
                      </div>
                      <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                        {formatCurrency(projection.fundeb.vaatFinal)}
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        VAAT calculado: {formatCurrencyDecimal(projection.fundeb.vaat.vaat)}/aluno
                      </p>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        (mínimo nacional: {formatCurrencyDecimal(FUNDEB_CONFIG.VAAT_MINIMO_NACIONAL)})
                      </p>
                    </div>

                    {/* VAAF */}
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-800/50' : 'bg-white/70'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-xs font-semibold ${darkMode ? 'text-[#2E4974]' : 'text-[#2E4974]'}`}>VAAF</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${projection.fundeb.vaaf.elegivel
                            ? 'bg-[#13983D]/20 text-[#13983D]'
                            : 'bg-slate-500/20 text-slate-500'
                          }`}>
                          {projection.fundeb.vaaf.elegivel ? 'Com complementação' : 'Sem complementação'}
                        </span>
                      </div>
                      <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                        {formatCurrency(projection.fundeb.vaafFinal)}
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        VAAF calculado: {formatCurrencyDecimal(projection.fundeb.vaaf.vaaf)}/aluno
                      </p>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        (mínimo nacional: {formatCurrencyDecimal(FUNDEB_CONFIG.VAAF_MINIMO_NACIONAL)})
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Impacto Financeiro Detalhado */}
              <div className={`rounded-2xl p-6 ${darkMode
                ? 'bg-gradient-to-r from-sideb-blue/20 to-sideb-green/20 border border-sideb-blue/30'
                : 'bg-gradient-to-r from-sideb-blue/10 to-sideb-green/10 border border-sideb-blue/20 shadow-sm'}`}>
                <h4 className={`font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                  Composição do Valor Total Projetado
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <p className={`text-xs mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Ganho IDEB/Ano</p>
                    <p className={`text-lg font-bold ${projection.additionalRevenue > 0 ? (darkMode ? 'text-[#13983D]' : 'text-[#13983D]') : (darkMode ? 'text-slate-400' : 'text-slate-500')}`}>
                      +{formatCurrency(projection.additionalRevenue)}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>VAAR</p>
                    <p className={`text-lg font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                      +{formatCurrency(projection.fundeb.vaarFinal)}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>VAAT</p>
                    <p className={`text-lg font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                      +{formatCurrency(projection.fundeb.vaatFinal)}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>VAAF</p>
                    <p className={`text-lg font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                      +{formatCurrency(projection.fundeb.vaafFinal)}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>VAAF Estadual</p>
                    <p className={`text-lg font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                      +{formatCurrency(vaafEstadual)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabela Comparativa */}
              <div className={`rounded-2xl overflow-hidden border ${darkMode ? 'border-slate-700' : 'border-slate-200 shadow-sm'}`}>
                <div className={`px-5 py-4 ${darkMode ? 'bg-slate-800/70' : 'bg-white'}`}>
                  <h5 className={`text-sm font-semibold flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    Comparativo de Cenários
                  </h5>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className={darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}>
                      <tr>
                        {['Cenário', 'IDEB', 'Faixa', 'Receita/Ano', 'Ganho Total/Ano', '4 Anos'].map(h => (
                          <th key={h} className={`px-4 py-2.5 text-xs font-medium ${h === 'Cenário' ? 'text-left' : 'text-center'} ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={darkMode ? 'divide-y divide-slate-700' : 'divide-y divide-slate-100'}>
                      {/* Atual */}
                      <tr className={darkMode ? 'bg-slate-800/30' : 'bg-white'}>
                        <td className={`px-4 py-3 font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Atual</td>
                        <td className={`px-4 py-3 text-center font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{effectiveIdeb.toFixed(1)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${currentStatus.bg} ${currentStatus.color}`}>{currentStatus.label}</span>
                        </td>
                        <td className={`px-4 py-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatCurrency(projection.currentRevenue)}</td>
                        <td className={`px-4 py-3 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>-</td>
                        <td className={`px-4 py-3 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>-</td>
                      </tr>
                      {/* Cenarios */}
                      {allScenarios.map((s) => {
                        const status = getIdebStatus(s.projectedIdeb);
                        const isSelected = s.value === selectedIncrement;
                        return (
                          <tr
                            key={s.value}
                            className={`cursor-pointer transition-colors ${isSelected
                              ? (darkMode ? 'bg-sideb-blue/20' : 'bg-sideb-blue/5')
                              : (darkMode ? 'bg-slate-800/30 hover:bg-slate-700/30' : 'bg-white hover:bg-slate-50')}`}
                            onClick={() => setSelectedIncrement(s.value)}
                          >
                            <td className={`px-4 py-3 font-medium ${isSelected ? 'text-sideb-blue' : (darkMode ? 'text-slate-300' : 'text-slate-600')}`}>
                              {s.label} {isSelected && '✓'}
                            </td>
                            <td className={`px-4 py-3 text-center font-semibold ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>{s.projectedIdeb.toFixed(1)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${status.bg} ${status.color}`}>{status.label}</span>
                            </td>
                            <td className={`px-4 py-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatCurrency(s.projectedRevenueWithExtras)}</td>
                            <td className={`px-4 py-3 text-center font-semibold ${s.totalGain > 0 ? (darkMode ? 'text-[#13983D]' : 'text-[#13983D]') : (darkMode ? 'text-slate-500' : 'text-slate-400')}`}>
                              {s.totalGain > 0 ? `+${formatCurrency(s.totalGain)}` : '-'}
                            </td>
                            <td className={`px-4 py-3 text-center font-semibold ${s.fourYearImpact > 0 ? (darkMode ? 'text-[#13983D]' : 'text-[#13983D]') : (darkMode ? 'text-slate-500' : 'text-slate-400')}`}>
                              {s.fourYearImpact > 0 ? `+${formatCurrency(s.fourYearImpact)}` : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Fórmulas FUNDEB */}
              <div className={`rounded-2xl p-5 ${darkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-slate-50 border border-slate-200'}`}>
                <h5 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  Fórmulas Utilizadas (FUNDEB)
                </h5>
                <div className={`text-xs space-y-2 font-mono ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  <p><strong>Alunos Ponderados</strong> = Matrículas × Fator de Ponderação</p>
                  <p><strong>VAAT</strong> = Receita Total Educação ÷ Alunos Ponderados (complementa se &lt; {formatCurrencyDecimal(FUNDEB_CONFIG.VAAT_MINIMO_NACIONAL)})</p>
                  <p><strong>VAAF</strong> = Receita FUNDEB ÷ Alunos Ponderados (complementa se &lt; {formatCurrencyDecimal(FUNDEB_CONFIG.VAAF_MINIMO_NACIONAL)})</p>
                  <p><strong>VAAR</strong> = {formatCurrencyDecimal(FUNDEB_CONFIG.VAAR_VALOR_BASE)}/aluno se crescimento IDEB ≥ {FUNDEB_CONFIG.IDEB_CRESCIMENTO_MINIMO} pts</p>
                  <p><strong>Valor Total</strong> = Ganho IDEB + VAAR + VAAT + VAAF + VAAF Estadual</p>
                </div>
              </div>

              {/* Dicas */}
              <div className={`rounded-2xl p-5 ${darkMode ? 'bg-amber-900/20 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
                <h5 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                  Onde Focar para Maximizar o Retorno
                </h5>
                <ul className={`text-xs space-y-1 ${darkMode ? 'text-amber-400/80' : 'text-amber-600'}`}>
                  <li>• <strong>Alfabetização na Idade Certa:</strong> Foco no 2º ano (peso alto no IQE)</li>
                  <li>• <strong>Proficiência no 5º ano:</strong> Compõe a base do cálculo do IDEB</li>
                  <li>• <strong>Busca Ativa:</strong> Garantir presença dos alunos nas avaliações (SIAVE/SAEB)</li>
                  <li>• <strong>Redução da Evasão:</strong> Cada aluno ausente impacta negativamente</li>
                  <li>• <strong>VAAR:</strong> Crescimento mínimo de {FUNDEB_CONFIG.IDEB_CRESCIMENTO_MINIMO} pontos no IDEB para elegibilidade</li>
                </ul>
              </div>

              {/* Nota legal */}
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                * Valores baseados na Lei do ICMS Educacional da Paraíba (Lei nº 12.373/2022) e parâmetros FUNDEB.
                Os valores apresentados são <strong>estimativas</strong> baseadas em parâmetros públicos e <strong>não substituem</strong> os cálculos oficiais realizados pelo FNDE/MEC.
                O recurso é recorrente enquanto o município mantiver a nota.
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default SimuladorFinanceiro;